import {
	type ActorRefFrom,
	assign,
	fromCallback,
	fromPromise,
	setup,
	type StateFrom,
} from 'xstate';

import { SKIP_PREFERRED_SITE_REDIRECT_QUERY_PARAM } from 'constants/queryParams';
import { useToastContext } from 'contexts';
import { useUpdateConsent } from 'hooks';
import {
	useLoginRenewalGTM,
	useLogoutGTM,
	useUserTypeUpdateGTM,
} from 'hooks/gtm';
import type { ConnectJuridicalType } from 'models/connect/oauth';
import type { MarketCode } from 'models/sitecore';
import { makeImplementationWarning } from 'state-machines/helpers';
import type { IntrospectionResponse } from 'utils/business-logic';
import { offGlobalEvent, onGlobalEvent, sendGlobalEvent } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import { requestSessionEndpoint, requestTokenData } from './session.actors';
import type {
	SessionMachineContext,
	SessionMachineEvents,
	SessionMachineInput,
} from './session.types';

const FORCE_END_SESSION_RELOAD_BACKOFF_DELAY_KEY =
	'forceEndSessionReloadBackoff';
function getStorageBackoffDelay() {
	return Number.parseInt(
		globalThis.sessionStorage.getItem(
			FORCE_END_SESSION_RELOAD_BACKOFF_DELAY_KEY,
		) ?? '1',
		10,
	);
}
const sessionMachine = setup({
	// oxlint-disable-next-line typescript/no-unnecessary-type-assertion
	types: {} as {
		context: SessionMachineContext;
		events: SessionMachineEvents;
		input: SessionMachineInput;
	},
	guards: {
		isAuthenticatedUser: (_, params: { statusCode: number }) =>
			params.statusCode === 200,
		shouldSkipAuth: ({ context }) => context.skipAuth,
		shouldRefreshBrowser: (_, params: { statusCode: number }) =>
			params.statusCode === 201,
	},
	actions: {
		setUserData: assign({
			userData: (_, params: { tokenData: IntrospectionResponse | undefined }) =>
				params.tokenData,
		}),
		unlock: () => {
			sendGlobalEvent('unlock');
		},
		lock: () => {
			sendGlobalEvent('lock');
		},
		setTokenExpiry: (_, params: { tokenExpiry: number }) => {
			globalThis.localStorage.setItem(
				'tokenExpiry',
				params.tokenExpiry.toString(),
			);
		},
		sessionEndedPageReload: () => {
			const url = new URL(globalThis.location.href);
			url.searchParams.set(SKIP_PREFERRED_SITE_REDIRECT_QUERY_PARAM, 'true');
			globalThis.location.assign(url.toString());
		},
		refreshAndReturnToRoot: () => {
			globalThis.location.assign(
				`/?${SKIP_PREFERRED_SITE_REDIRECT_QUERY_PARAM}=true`,
			);
		},
		pushLogoutToGTM: makeImplementationWarning('pushLogoutToGTM'),
		pushUserTypeUpdateToGTM: makeImplementationWarning(
			'pushUserTypeUpdateToGTM',
		),
		sendLoginRenewalToGTM: makeImplementationWarning('sendLoginRenewalToGTM'),
		incSessionEndpointFailedRequests: assign({
			sessionEndpointFailedRequests: ({ context }) =>
				context.sessionEndpointFailedRequests + 1,
		}),
		showLogoutErrorToast: makeImplementationWarning('showLogoutErrorToast'),
		updateConsent: makeImplementationWarning('updateConsent'),
		addToBackoff: () => {
			globalThis.sessionStorage.setItem(
				FORCE_END_SESSION_RELOAD_BACKOFF_DELAY_KEY,
				(getStorageBackoffDelay() + 1).toString(),
			);
		},
	},
	actors: {
		requestSessionEndpoint: fromPromise(
			({
				input,
			}: {
				input: {
					juridicalType: ConnectJuridicalType;
					logout?: 'true';
					marketCode: MarketCode | undefined;
				};
			}) =>
				requestSessionEndpoint({
					juridicalType: input.juridicalType,
					// @ts-expect-error This will probably always be defined, but make sure,
					// somewhere.
					marketCode: input.marketCode,
					logout: input.logout,
				}),
		),
		checkUserStatus: fromPromise(() => requestTokenData()),
		listenForWindowEvent: fromCallback(({ sendBack }) => {
			const refresh = () => {
				sendBack({ type: 'REFRESH' });
			};
			const forceEndSession = () => {
				sendBack({ type: 'FORCE_END_SESSION' });
			};

			onGlobalEvent('refresh-token', refresh);
			onGlobalEvent('force-end-session', forceEndSession);
			return () => {
				offGlobalEvent('refresh-token', refresh);
				offGlobalEvent('force-end-session', forceEndSession);
			};
		}),
	},
	delays: {
		// Delay for attempting to get or check token, one second per failed
		// attempt up to a maximum of 5 seconds.
		RETRY_DELAY: ({ context }) =>
			Math.min(context.sessionEndpointFailedRequests || 1, 5) * 1000,
		backoff: () => getStorageBackoffDelay() * 2000,
	},
}).createMachine({
	id: 'session',
	initial: 'initialisation',
	context: ({ input }) => ({
		skipAuth: input.skipAuth,
		isAuthenticated: false,
		sessionEndpointFailedRequests: 0,
		juridicalType: input.juridicalType,
		organizationDetailUrl: input.organizationDetailUrl,
		marketCode: input.marketCode,
	}),
	states: {
		initialisation: {
			tags: ['initialisation'],
			entry: 'lock',
			always: [
				{
					target: 'skipAuth',
					guard: 'shouldSkipAuth',
				},
				{
					target: 'checkTokenStatus',
				},
			],
		},
		skipAuth: {
			description:
				'If we are in the experience editor skip auth will be set and we do not continue auth flow.',
			type: 'final',
		},
		checkTokenStatus: {
			tags: ['initialisation'],
			description: `Send request to /session endpoint to get token status.
				If 201 we reload the page and end up here again.`,
			invoke: {
				id: 'requestTokenStatus',
				src: 'requestSessionEndpoint',
				input: ({ context }) => ({
					juridicalType: context.juridicalType,
					marketCode: context.marketCode,
				}),
				onDone: [
					{
						target: '#delayedBrowserRefresh',
						guard: {
							type: 'shouldRefreshBrowser',
							params: ({ event }) => ({
								statusCode: event.output.statusCode,
							}),
						},
					},
					{
						target: 'checkUserStatus',
						actions: [
							{
								type: 'setTokenExpiry',
								params: ({ event }) => ({
									tokenExpiry: event.output.tokenData.tokenExpiry,
								}),
							},
						],
					},
				],
				onError: {
					target: '#sessionEndpointRequestFailed',
				},
			},
		},
		checkUserStatus: {
			tags: ['initialisation'],
			description: `We introspect the token and check if the user is logged in or not.
				If logged in we expect 200 and token data that we set to authUser.
				If not logged in we expect 204 and no data.`,
			invoke: {
				id: 'checkUserStatus',
				src: 'checkUserStatus',
				onDone: [
					{
						target: '#authenticatedUser',
						guard: {
							type: 'isAuthenticatedUser',
							params: ({ event }) => ({
								statusCode: event.output.statusCode,
							}),
						},
						actions: [
							{
								type: 'setUserData',
								params: ({ event }) => ({
									tokenData: event.output.tokenData,
								}),
							},
						],
					},
					{
						target: '#authenticatedAnon',
					},
				],
			},
		},
		authenticatedAnon: {
			tags: ['authenticated', 'authenticatedAnon'],
			id: 'authenticatedAnon',
			entry: ['unlock', 'pushUserTypeUpdateToGTM'],
			initial: 'idle',
			invoke: {
				src: 'listenForWindowEvent',
			},
			states: {
				idle: {
					on: {
						REFRESH: {
							target: 'refreshingToken',
						},
					},
				},
				refreshingToken: {
					entry: 'lock',
					invoke: {
						id: 'getRefreshToken',
						src: 'requestSessionEndpoint',
						input: ({ context }) => ({
							juridicalType: context.juridicalType,
							marketCode: context.marketCode,
						}),
						onDone: [
							{
								target: '#delayedBrowserRefresh',
								guard: {
									type: 'shouldRefreshBrowser',
									params: ({ event }) => ({
										statusCode: event.output.statusCode,
									}),
								},
							},
							{
								target: 'idle',
								actions: [
									{
										type: 'setTokenExpiry',
										params: ({ event }) => ({
											tokenExpiry: event.output.tokenData.tokenExpiry,
										}),
									},
									'unlock',
								],
							},
						],
						onError: {
							target: '#sessionEndpointRequestFailed',
						},
					},
				},
			},
		},
		authenticatedUser: {
			tags: ['authenticated', 'authenticatedUser'],
			id: 'authenticatedUser',
			entry: ['unlock', 'pushUserTypeUpdateToGTM', 'updateConsent'],
			initial: 'idle',
			invoke: {
				src: 'listenForWindowEvent',
			},
			states: {
				idle: {
					on: {
						REFRESH: {
							target: 'refreshingToken',
						},
					},
				},
				refreshingToken: {
					entry: 'lock',
					invoke: {
						id: 'getRefreshToken',
						src: 'requestSessionEndpoint',
						input: ({ context }) => ({
							juridicalType: context.juridicalType,
							marketCode: context.marketCode,
						}),
						onDone: [
							{
								target: '#delayedBrowserRefresh',
								guard: {
									type: 'shouldRefreshBrowser',
									params: ({ event }) => ({
										statusCode: event.output.statusCode,
									}),
								},
							},
							{
								target: 'idle',
								actions: [
									{
										type: 'setTokenExpiry',
										params: ({ event }) => ({
											tokenExpiry: event.output.tokenData.tokenExpiry,
										}),
									},
									'unlock',
									'sendLoginRenewalToGTM',
								],
							},
						],
						onError: {
							target: '#sessionEndpointRequestFailed',
						},
					},
				},
				logout: {
					id: 'logout',
					tags: ['logout'],
					entry: 'pushLogoutToGTM',
					invoke: {
						src: 'requestSessionEndpoint',
						input: ({ context }) => ({
							juridicalType: context.juridicalType,
							marketCode: context.marketCode,
							logout: 'true',
						}),
						onDone: { actions: 'refreshAndReturnToRoot' },
						onError: {
							target: 'idle',
							actions: 'showLogoutErrorToast',
						},
					},
				},
			},
			on: {
				LOGOUT: {
					target: '#logout',
				},
			},
		},
		delayedBrowserRefresh: {
			id: 'delayedBrowserRefresh',
			after: {
				2000: { actions: 'sessionEndedPageReload' },
			},
		},
		sessionEndpointRequestFailed: {
			id: 'sessionEndpointRequestFailed',
			entry: 'incSessionEndpointFailedRequests',
			always: [
				{
					target: 'showFailure',
				},
			],
		},
		showFailure: {
			after: {
				RETRY_DELAY: { target: 'initialisation' },
			},
		},
		errorVerifying: {
			always: [
				{
					target: 'showFailure',
				},
			],
		},
		forceEndSession: {
			id: 'forceEndSession',
			// Used when authenticated requests going via the fetch helper get several consecutive 401 responses.
			// trigger a session logout/end session, hoping the session endpoint is ok,
			// and reload the page, hoping the session startup fixes the issue.
			initial: 'requestLogout',
			states: {
				requestLogout: {
					invoke: {
						src: 'requestSessionEndpoint',
						input: ({ context }) => ({
							juridicalType: context.juridicalType,
							marketCode: context.marketCode,
							logout: 'true',
						}),
						onDone: { target: 'reloadPage' },
						onError: { target: 'reloadPage' },
					},
				},
				reloadPage: {
					after: {
						backoff: {
							actions: ['addToBackoff', 'sessionEndedPageReload'],
						},
					},
				},
			},
		},
	},

	on: {
		LOGOUT: {
			target: '#logout',
		},
		FORCE_END_SESSION: {
			target: '#forceEndSession',
		},
	},
});
export type SessionMachineState = StateFrom<typeof sessionMachine>;
export type SessionMachineActor = ActorRefFrom<typeof sessionMachine>;

export function useSessionMachine() {
	const { t } = useI18n();
	const { sendToast } = useToastContext();
	const { updateConsent } = useUpdateConsent();
	const { sendLoginRenewalEvent } = useLoginRenewalGTM();
	const { sendLogoutEvent } = useLogoutGTM();
	const { sendUserTypeUpdateEvent } = useUserTypeUpdateGTM();

	return sessionMachine.provide({
		actions: {
			showLogoutErrorToast: () => {
				sendToast({ message: t('logout_error'), variant: 'error' });
			},
			updateConsent: () => {
				updateConsent();
			},
			pushLogoutToGTM: () => {
				sendLogoutEvent();
			},
			pushUserTypeUpdateToGTM: () => {
				sendUserTypeUpdateEvent();
			},
			sendLoginRenewalToGTM: () => {
				sendLoginRenewalEvent();
			},
		},
	});
}
